import { Button } from "@/components/ui/button";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
    DropdownMenuSeparator
} from "@/components/ui/dropdown-menu";
import { usePortalContainer } from "@/common/hooks/use-portal-container";
import { MoreHorizontal, ExternalLink, Pen, Trash2 } from "lucide-react";
import { useState, useEffect } from "react";
import { useAppDispatch, useAppSelector } from "@common-reducers/hooks/store.hook";
import { appConfig } from "@common-config/app.config";
import { isExtensionContextOrWhatsappView } from "@common-services/detect-context";
import { pickerDialogActions } from "@common-reducers/PickerDialogReducer";
import { boardsActions } from "@common-reducers/BoardsReducer";
import { taskActions } from "@common-reducers/TasksReducer";
import { isUserBoardOwner, isCurrentWorkspaceOwnerSelector } from "@common-reducers/BoardsSelectors";
import { UndoRedoAction } from "@common-reducers/UndoRedoReducer";
import { doActionThunk } from "@common-reducers/UndoRedoThunks";
import PromptDeleteAction from "@common-components/prompt-delete-action/prompt-delete-action";
import { boardsSelectOneFieldByQuery } from "@common-reducers/DBServiceThunks";
import { MODAL, modalActions } from "@common-reducers/ModalReducer";
import { useTranslation } from "react-i18next";
import { namespace } from "@common-config/constants";
import { FileDown, FileSpreadsheet } from "lucide-react";

interface BoardMenuPickerShadcnProps {
    boardId: string;
    className?: string;
    /** The menu, or the delete prompt it opened, is open — the "Board options" tooltip keeps itself shut. */
    onOpenChange?: (open: boolean) => void;
}

export default function BoardMenuPickerShadcn({ boardId, className, onOpenChange }: BoardMenuPickerShadcnProps) {
    const dispatch = useAppDispatch();
    
    // Portal logic
    const localPortalContainer = usePortalContainer();
    const [portalContainer, setPortalContainer] = useState<HTMLElement | ShadowRoot | null>(null);

    useEffect(() => {
        const globalPortal = document.getElementById('labels-modals')?.shadowRoot?.querySelector('.btcss-portal-container');
        if (globalPortal) {
            setPortalContainer(globalPortal as HTMLElement);
        }
    }, []);

    // Selectors
    const isBoardOwner = useAppSelector(state => isUserBoardOwner(state, { boardId }));
    const isCurrentWorkspaceOwner = useAppSelector(isCurrentWorkspaceOwnerSelector);
    const boardName = useAppSelector(state => boardsSelectOneFieldByQuery(state.DBBoardsReducer, {
        _id: boardId
    }, 'name'));
    const { t } = useTranslation(namespace);

    // State
    const [deletePromptOpen, setDeletePromptOpen] = useState(false);
    const [menuOpen, setMenuOpen] = useState(false);
    // The delete prompt renders inside this component, so hovering it would bubble to the
    // tooltip around the trigger like the menu does: report both.
    const busy = menuOpen || deletePromptOpen;
    useEffect(() => { onOpenChange?.(busy); }, [busy, onOpenChange]);

    // Actions
    const handleOpenBoardInNewTab = () => {
        const boardURL = `${appConfig.APP_ENDPOINT}/tasks/?boardID=${boardId}`;
        if (isExtensionContextOrWhatsappView()) {
            window.open(boardURL, "_blank");
        } else {
            window.open(boardURL, '_blank');
        }
        setMenuOpen(false);
    };

    const handleImport = () => {
         dispatch(modalActions.setComponentToRender(MODAL.IMPORT_EXCEL_TO_BLUETICKS));
         setMenuOpen(false);
    };

    const handleExport = () => {
        dispatch(modalActions.setComponentToRender(MODAL.EXPORT_TO_EXCEL));
        setMenuOpen(false);
    };

    const handleRenameBoard = () => {
        dispatch(boardsActions.setRenameBoard(true)); // Updated to setRenameBoard(true) to match BoardMenuOptions
        setMenuOpen(false);
    };

    const handleDeleteClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        setMenuOpen(false);
        setDeletePromptOpen(true);
    };

    const handleDeleteConfirmationClosed = (shouldDelete?: boolean) => {
        if (shouldDelete === true) {
            const action: UndoRedoAction<string[], string> = {
                type: 'delete-board-list',
                data: [boardId],
                deletedOrArchived: "deleted"
            };
            dispatch(doActionThunk({ action }));
            dispatch(taskActions.setTaskViewSideDrawerOpenWith("")); // Close drawer if open
        }
        setDeletePromptOpen(false);
    };

    return (
        <>
            <PromptDeleteAction 
                title={`Delete board ${boardName} ?`} 
                handleClose={handleDeleteConfirmationClosed} 
                open={deletePromptOpen} 
            />
            
            <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
                <DropdownMenuTrigger asChild>
                    <Button 
                        variant="ghost" 
                        size="icon" 
                        className={`h-8 w-8 text-gray-500 hover:text-gray-900 ${className}`}
                    >
                        <MoreHorizontal className="h-4 w-4" />
                    </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent 
                    align="end" 
                    className="w-56 z-[9999]"
                    container={portalContainer || localPortalContainer}
                >
                    <DropdownMenuItem onClick={handleOpenBoardInNewTab} className="cursor-pointer">
                        <ExternalLink className="h-4 w-4 mr-2" />
                        <span>{t('boardMenuOptions.openBoardInNewTab')}</span>
                    </DropdownMenuItem>

                    <DropdownMenuItem onClick={handleImport} className="cursor-pointer">
                        <FileDown className="h-4 w-4 mr-2" />
                        <span>{t('boardMenuOptions.import')}</span>
                    </DropdownMenuItem>

                    <DropdownMenuItem onClick={handleExport} className="cursor-pointer">
                        <FileSpreadsheet className="h-4 w-4 mr-2" />
                        <span>{t('boardMenuOptions.exportBoardToExcel')}</span>
                    </DropdownMenuItem>
                    
                    <DropdownMenuItem onClick={handleRenameBoard} className="cursor-pointer">
                        <Pen className="h-4 w-4 mr-2" />
                        <span>{t('boardMenuOptions.renameBoard')}</span>
                    </DropdownMenuItem>
                    
                    <DropdownMenuSeparator className="bg-gray-200" />
                    
                    <DropdownMenuItem 
                        onClick={handleDeleteClick} 
                        className="cursor-pointer text-red-600 focus:text-red-600 focus:bg-red-50"
                        disabled={!(isBoardOwner || isCurrentWorkspaceOwner)}
                    >
                        <Trash2 className="h-4 w-4 mr-2" />
                        <span>{t('boardMenuOptions.deleteBoard')}</span>
                    </DropdownMenuItem>
                </DropdownMenuContent>
            </DropdownMenu>
        </>
    );
}
